import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import { onAuthStateChanged, signInWithEmailAndPassword, signOut as firebaseSignOut } from "firebase/auth";
import { collection, query, where, getDocs, updateDoc, doc } from "firebase/firestore";
import { auth, db } from "../firebase/config";
import { logSecurityEvent } from "../services/AuditLogger";
import { recordUserLogin } from "../services/userService";
import SessionTimeoutWarningModal from "../components/modals/SessionTimeoutWarningModal";

const AuthContext = createContext();

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes total
const WARNING_COUNTDOWN_SECONDS = 120; // 2 minutes warning countdown

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  return useContext(AuthContext);
};

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [warningOpen, setWarningOpen] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(WARNING_COUNTDOWN_SECONDS);
  const [mfaPending, setMfaPending] = useState(false);
  const [mfaDetails, setMfaDetails] = useState(null);
  const [pendingAuthUser, setPendingAuthUser] = useState(null);

  const lastActivityRef = useRef(Date.now());
  const checkIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (warningOpen) {
      setWarningOpen(false);
      setRemainingSeconds(WARNING_COUNTDOWN_SECONDS);
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    }
  }, [warningOpen]);

  async function checkUserMFA(authUser) {
    if (!authUser || !authUser.email) return { mfaRequired: false };
    try {
      const q = query(collection(db, "users"), where("email", "==", authUser.email));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const userDoc = snap.docs[0];
        const data = userDoc.data();
        if (data.mfaEnabled === true && data.mfaSecret) {
          return {
            mfaRequired: true,
            userDocId: userDoc.id,
            mfaSecret: data.mfaSecret,
            mfaBackupCodes: data.mfaBackupCodes || []
          };
        }
      }
    } catch (e) {
      console.error("Error checking user MFA status:", e);
    }
    return { mfaRequired: false };
  }

  async function login(email, password) {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const mfaCheck = await checkUserMFA(cred.user);
      
      if (mfaCheck.mfaRequired) {
        setPendingAuthUser(cred.user);
        setMfaDetails({
          email: cred.user.email,
          userDocId: mfaCheck.userDocId,
          secret: mfaCheck.mfaSecret,
          backupCodes: mfaCheck.mfaBackupCodes
        });
        setMfaPending(true);
        setCurrentUser(null);
        return { mfaRequired: true };
      }

      logSecurityEvent({
        action: "AUTH_LOGIN_SUCCESS",
        category: "AUTH",
        userId: cred.user.uid,
        userEmail: cred.user.email,
        status: "SUCCESS",
        severity: "INFO",
        details: { method: "password" }
      });
      sessionStorage.setItem("mfa_verified_" + cred.user.uid, "true");
      sessionStorage.setItem("last_login_recorded_" + cred.user.uid, "true");
      recordUserLogin(cred.user);
      lastActivityRef.current = Date.now();
      setCurrentUser(cred.user);
      return { mfaRequired: false, user: cred.user };
    } catch (err) {
      logSecurityEvent({
        action: "AUTH_LOGIN_FAILURE",
        category: "AUTH",
        userEmail: email,
        status: "FAILURE",
        severity: "WARNING",
        details: { error: err.message }
      });
      throw err;
    }
  }

  async function completeMfaLogin({ usedBackupCode, remainingBackupCodes }) {
    if (!pendingAuthUser) return;

    try {
      if (usedBackupCode && mfaDetails?.userDocId) {
        await updateDoc(doc(db, "users", mfaDetails.userDocId), {
          mfaBackupCodes: remainingBackupCodes || []
        });

        logSecurityEvent({
          action: "AUTH_MFA_RECOVERY_USED",
          category: "AUTH",
          userId: pendingAuthUser.uid,
          userEmail: pendingAuthUser.email,
          status: "SUCCESS",
          severity: "WARNING",
          details: { remainingCodes: (remainingBackupCodes || []).length }
        });
      } else {
        logSecurityEvent({
          action: "AUTH_MFA_SUCCESS",
          category: "AUTH",
          userId: pendingAuthUser.uid,
          userEmail: pendingAuthUser.email,
          status: "SUCCESS",
          severity: "INFO",
          details: { method: "TOTP_RFC6238" }
        });
      }

      sessionStorage.setItem("mfa_verified_" + pendingAuthUser.uid, "true");
      sessionStorage.setItem("last_login_recorded_" + pendingAuthUser.uid, "true");
      recordUserLogin(pendingAuthUser);
      setCurrentUser(pendingAuthUser);
      setPendingAuthUser(null);
      setMfaPending(false);
      setMfaDetails(null);
      lastActivityRef.current = Date.now();
    } catch (err) {
      console.error("Error completing MFA login:", err);
      throw err;
    }
  }

  async function cancelMfaLogin() {
    setMfaPending(false);
    setMfaDetails(null);
    setPendingAuthUser(null);
    setCurrentUser(null);
    return firebaseSignOut(auth);
  }

  async function logout(reason = "USER_REQUEST") {
    if (currentUser) {
      sessionStorage.removeItem("mfa_verified_" + currentUser.uid);
      logSecurityEvent({
        action: reason === "SESSION_TIMEOUT" ? "AUTH_SESSION_TIMEOUT" : "AUTH_LOGOUT",
        category: "AUTH",
        userId: currentUser.uid,
        userEmail: currentUser.email,
        status: "SUCCESS",
        severity: reason === "SESSION_TIMEOUT" ? "WARNING" : "INFO",
        details: { reason }
      });
    }
    setWarningOpen(false);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    return firebaseSignOut(auth);
  }

  // Auth State Listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setCurrentUser(null);
        setPendingAuthUser(null);
        setMfaPending(false);
        setMfaDetails(null);
        setLoading(false);
        return;
      }

      // Check if session has already passed MFA in current tab session
      const sessionVerified = sessionStorage.getItem("mfa_verified_" + user.uid) === "true";
      if (sessionVerified) {
        setCurrentUser(user);
        if (!sessionStorage.getItem("last_login_recorded_" + user.uid)) {
          sessionStorage.setItem("last_login_recorded_" + user.uid, "true");
          recordUserLogin(user);
        }
        lastActivityRef.current = Date.now();
        setLoading(false);
        return;
      }

      // Check if MFA is enabled
      const mfaCheck = await checkUserMFA(user);
      if (mfaCheck.mfaRequired) {
        setPendingAuthUser(user);
        setMfaDetails({
          email: user.email,
          userDocId: mfaCheck.userDocId,
          secret: mfaCheck.mfaSecret,
          backupCodes: mfaCheck.mfaBackupCodes
        });
        setMfaPending(true);
        setCurrentUser(null);
      } else {
        sessionStorage.setItem("mfa_verified_" + user.uid, "true");
        if (!sessionStorage.getItem("last_login_recorded_" + user.uid)) {
          sessionStorage.setItem("last_login_recorded_" + user.uid, "true");
          recordUserLogin(user);
        }
        setCurrentUser(user);
        lastActivityRef.current = Date.now();
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Inactivity & Session Monitor
  useEffect(() => {
    if (!currentUser) {
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      return;
    }

    const activityEvents = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    const handleUserInteraction = () => {
      // If warning modal is not active, treat any interaction as activity refresh
      if (!warningOpen) {
        lastActivityRef.current = Date.now();
      }
    };

    activityEvents.forEach((evt) => window.addEventListener(evt, handleUserInteraction, { passive: true }));

    // Check inactivity every 10 seconds
    checkIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const idleTime = now - lastActivityRef.current;
      const timeUntilTimeout = INACTIVITY_TIMEOUT_MS - idleTime;

      if (timeUntilTimeout <= WARNING_COUNTDOWN_SECONDS * 1000 && !warningOpen) {
        // Open warning modal and begin precise countdown
        const initialSeconds = Math.max(1, Math.floor(timeUntilTimeout / 1000));
        setRemainingSeconds(initialSeconds);
        setWarningOpen(true);
      }
    }, 10000);

    return () => {
      activityEvents.forEach((evt) => window.removeEventListener(evt, handleUserInteraction));
      if (checkIntervalRef.current) clearInterval(checkIntervalRef.current);
    };
  }, [currentUser, warningOpen]);

  // Warning Modal Countdown Timer
  useEffect(() => {
    if (warningOpen) {
      countdownIntervalRef.current = setInterval(() => {
        setRemainingSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            logout("SESSION_TIMEOUT");
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      };
    }
  }, [warningOpen]);

  const value = {
    currentUser,
    login,
    logout,
    loading,
    mfaPending,
    mfaDetails,
    completeMfaLogin,
    cancelMfaLogin
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
      <SessionTimeoutWarningModal
        isOpen={warningOpen}
        remainingSeconds={remainingSeconds}
        onStayLoggedIn={resetActivity}
        onLogout={() => logout("USER_REQUEST")}
      />
    </AuthContext.Provider>
  );
}
